<script lang="ts" setup></script>
<template>
    <div class="confidential-policy-page">
        <h1 class="text-5xl text-center font-semibold">
            Politiques de confidentialité
        </h1>
        <!-- 1. Introduction -->
        <div class="confidential-policy-page__section">
            <h2 class="font-semibold text-3xl">1. Introduction</h2>
            <p>
                Dans le cadre de notre participation au programme gouvernemental
                <strong>Watt Watchers</strong>, Homeys agit en tant que
                <strong>pilote du programme</strong>, en collaboration avec
                <strong
                    >l’Association Watt Watchers, l’État français,
                    l’ADEME</strong
                >
                et d’autres partenaires. Le programme, validé par
                <strong>l’arrêté du 2 mai 2024</strong> et financé par le
                dispositif des
                <strong>Certificats d’Économies d’Énergie (CEE)</strong>, a pour
                objectif principal de
                <strong
                    >mesurer les économies d’énergie réelles après des travaux
                    de rénovation énergétique</strong
                >.
            </p>
            <p>
                Homeys s’engage à protéger vos données personnelles, en
                conformité avec le
                <strong
                    >Règlement Général sur la Protection des Données
                    (RGPD)</strong
                >
                et la législation française applicable. Cette politique de
                confidentialité vise à vous informer sur la nature des données
                collectées, leur utilisation, leur conservation, ainsi que sur
                vos droits.
            </p>
            <p>
                La convention signée entre l’État, l’ADEME, l’Association Watt
                Watchers et les pilotes est consultable ici :
                <BaseButton
                    variant="link"
                    as="a"
                    size="small"
                    class="button-link"
                    href="https://www.ecologie.gouv.fr/sites/default/files/documents/Convention_Watt%20Watchers.pdf"
                    label="Consulter la convention Watt Watchers"
                />
            </p>
        </div>
        <!-- 2. Données personnelles collectées -->
        <div class="confidential-policy-page__section">
            <h2 class="font-semibold text-3xl">
                2. Données personnelles collectées
            </h2>
            <p>
                Dans le cadre du programme Watt Watchers, Homeys est amené à
                collecter les données personnelles suivantes :
            </p>
            <ol>
                <li>
                    <p><strong>Données d'identité et de contact</strong> :</p>
                    <ul>
                        <li>
                            Nom, prénom, adresse postale, adresse électronique,
                            numéro de téléphone.
                        </li>
                    </ul>
                </li>
                <li>
                    <p>
                        <strong
                            >Données relatives aux travaux de rénovation
                            énergétique</strong
                        >
                        :
                    </p>
                    <ul>
                        <li>
                            Nature des travaux effectués, date des travaux,
                            montant des travaux, dispositif FOST (fiche
                            d’opération standardisée), gains énergétiques
                            estimés en kWh cumac.
                        </li>
                    </ul>
                </li>
                <li>
                    <p>
                        <strong>Données de consommation énergétique</strong> :
                    </p>
                    <ul>
                        <li>
                            Courbes de charge issues des compteurs communicants
                            (en kWh).
                        </li>
                    </ul>
                </li>
                <li>
                    <p>
                        <strong
                            >Réponses aux questionnaires complémentaires
                        </strong>
                        :
                    </p>
                    <ul>
                        <li>
                            Informations additionnelles concernant les
                            comportements de consommation, les équipements
                            énergétiques et tout autre élément pertinent pour
                            l’étude.
                        </li>
                    </ul>
                </li>
            </ol>
            <p>
                Ces données peuvent également faire l’objet de traitements
                statistiques après <strong>pseudonymisation</strong>, notamment
                pour leur transmission au
                <strong
                    >CSTB (Centre Scientifique et Technique du Bâtiment)</strong
                >, dans le but d’optimiser les politiques publiques de sobriété
                énergétique.
            </p>
        </div>
        <!-- 3. Finalités de la collecte et base légale -->
        <div class="confidential-policy-page__section">
            <h2 class="font-semibold text-3xl">
                3. Finalités de la collecte et base légale
            </h2>
            <p>
                Les données collectées sont traitées pour les finalités
                suivantes :
            </p>
            <ol>
                <li>
                    <p>
                        <strong
                            >Participation au programme Watt Watchers</strong
                        >
                        :
                    </p>
                    <ul>
                        <li>
                            Collecter des données permettant de mesurer les
                            économies réelles réalisées après des travaux de
                            rénovation énergétique.
                        </li>
                    </ul>
                </li>
                <li>
                    <p>
                        <strong>
                            Analyse statistique et évaluation des politiques
                            publiques</strong
                        >
                        :
                    </p>
                    <ul>
                        <li>
                            Transmettre des données pseudonymisées au CSTB
                            (Centre Scientifique et Technique du Bâtiment) pour
                            des analyses statistiques.
                        </li>
                    </ul>
                </li>
                <li>
                    <p><strong>Conformité réglementaire</strong> :</p>
                    <ul>
                        <li>
                            Suivi et évaluation des résultats du programme
                            conformément aux exigences du dispositif CEE.
                        </li>
                    </ul>
                </li>
            </ol>
            <p><strong>Base légale du traitement</strong> :</p>
            <ul>
                <li>
                    <p>
                        <strong>Consentement</strong> : donné librement par
                        chaque ménage lors de son inscription au programme.
                    </p>
                </li>
                <li>
                    <p>
                        <strong>Intérêt légitime</strong>: traitement nécessaire
                        pour exécuter les missions confiées à Homeys dans le
                        cadre du programme.
                    </p>
                </li>
            </ul>
        </div>
        <!-- 4. Durée de conservation des données -->
        <div class="confidential-policy-page__section">
            <h2 class="font-semibold text-3xl">
                4. Durée de conservation des donnée
            </h2>
            <p>
                Les données personnelles sont conservées jusqu’au
                <strong>31 décembre 2027</strong>, date de fin du programme.
                Au-delà, elles seront <strong>supprimées</strong> ou
                <strong>anonymisées</strong>, sauf en cas de besoin à des fins
                de <strong>recherche</strong> ou
                <strong>évaluation d’intérêt public</strong>, dans le respect du
                cadre légal.
            </p>
        </div>
        <!-- 5. Partage des données -->
        <div class="confidential-policy-page__section">
            <h2 class="font-semibold text-3xl">5. Partage des donnée</h2>
            <p>
                Les données sont exclusivement partagées avec les partenaires
                strictement impliqués dans le programme :
            </p>
            <ul>
                <li>Données pseudonymisées transmises au CSTB</li>
                <li>
                    Aucun transfert à des acteurs commerciaux ou à des fins
                    publicitaires.
                </li>
            </ul>
        </div>
        <!-- 6. Vos droits -->
        <div class="confidential-policy-page__section">
            <h2 class="font-semibold text-3xl">6. Vos droits</h2>
            <p>
                En vertu du RGPD, vous bénéficiez des droits suivants concernant
                vos données personnelles :
            </p>
            <ol>
                <li>
                    <p>
                        <strong>Droit d'accès</strong> à vos données
                        personnelles
                    </p>
                </li>
                <li>
                    <p>
                        <strong>Droit de rectification</strong> si vos données
                        sont inexactes
                    </p>
                </li>
                <li>
                    <p>
                        <strong>Droit à l'effacement</strong>, sous réserve des
                        obligations légales
                    </p>
                </li>
                <li>
                    <p><strong>Droit à la limitation</strong> du traitement</p>
                </li>
                <li>
                    <p>
                        <strong>Droit à la portabilité</strong> de vos données
                    </p>
                </li>
                <li>
                    <p>
                        <strong>Droit de révocation</strong> de votre
                        consentement à tout moment
                    </p>
                </li>
            </ol>

            <p>Pour exercer vos droits, vous pouvez nous contacter :</p>
            <ul>
                <li>
                    Par courrier : Homeys, 30 rue Monsieur Le Prince, 75006
                    Paris
                </li>
                <li>
                    Par email :<BaseButton
                        variant="link"
                        as="a"
                        size="small"
                        class="button-link"
                        href="mailto:dpo@homeys.io"
                        label="dpo@homeys.io"
                    />
                </li>
            </ul>
        </div>
        <!-- 7. Mesures de sécurité  -->
        <div class="confidential-policy-page__section">
            <h2 class="font-semibold text-3xl">7. Mesures de sécurité</h2>
            <p>
                Homeys met en place des
                <strong
                    >mesures techniques et organisationnelles
                    appropriées</strong
                >
                pour protéger vos données contre tout accès non autorisé, perte,
                altération ou divulgation.
            </p>
        </div>
        <!-- 8. Modifications de la politique de confidentialité -->
        <div class="confidential-policy-page__section">
            <h2 class="font-semibold text-3xl">
                8. Modifications de la politique de confidentialité
            </h2>
            <p>
                Cette politique pourra être mise à jour en fonction de
                l’évolution du programme Watt Watchers ou de nouvelles
                obligations réglementaires. Vous serez informé(e) de toute
                modification importante par les canaux habituels.
            </p>
        </div>
        <!-- 9. Contact -->
        <div class="confidential-policy-page__section">
            <h2 class="font-semibold text-3xl">9. Contact</h2>
            <p>
                Pour toute question relative à cette politique ou à vos données
                personnelles, vous pouvez contacter notre
                <strong>Délégué à la Protection des Données (DPO)</strong> :
                <BaseButton
                    variant="link"
                    as="a"
                    size="small"
                    class="button-link"
                    href="mailto:dpo@homeys.io"
                    label="dpo@homeys.io"
                />
            </p>
        </div>
        <div class="confidential-policy-page__section">
            <h2 class="font-semibold text-3xl">
                10. Caractère obligatoire ou facultatif des données
            </h2>
            <p>
                Certaines données collectées dans le cadre du programme Watt
                Watchers sont obligatoires pour permettre la participation
                effective au programme et la réalisation des analyses prévues.
                D’autres données sont facultatives et visent à enrichir les
                résultats, notamment via les questionnaires complémentaires.
                Chaque formulaire précise le caractère obligatoire ou facultatif
                de chaque champ.
            </p>
        </div>
        <div class="confidential-policy-page__section">
            <h2 class="font-semibold text-3xl">
                11. Profilage et décisions automatisées
            </h2>
            <p>
                Dans le cadre du programme, une recherche automatisée peut être
                effectuée à partir de vos nom, prénom et adresse afin
                d’identifier votre numéro de compteur électrique. En dehors de
                cette fonctionnalité, aucune décision automatisée ni profilage
                au sens du RGPD n’est mis en œuvre.
            </p>
        </div>
        <div class="confidential-policy-page__section">
            <h2 class="font-semibold text-3xl">
                12. Transferts de données hors de l’Union Européenne
            </h2>
            <p>
                Les données personnelles collectées dans le cadre du programme
                <strong
                    >ne sont en aucun cas transférées en dehors de l’Union
                    Européenne</strong
                >. Elles sont hébergées et traitées exclusivement sur des
                serveurs situés dans l’UE ou dans des pays bénéficiant d’un
                niveau de protection reconnu comme adéquat par la Commission
                européenne.
            </p>
        </div>
        <div class="confidential-policy-page__section">
            <h2 class="font-semibold text-3xl">
                13. Réclamation auprès de la CNIL
            </h2>

            <p>
                Si vous estimez que le traitement de vos données personnelles
                constitue une violation du RGPD, vous avez le droit d’introduire
                une réclamation auprès de l’autorité de contrôle compétente, en
                France :
                <strong
                    >Commission Nationale de l’Informatique et des Libertés
                    (CNIL)</strong
                >
            </p>
            <p>3 Place de Fontenoy – TSA 80715 – 75334 Paris Cedex 07</p>
            <p>
                Site web :
                <BaseButton
                    variant="link"
                    as="a"
                    size="small"
                    class="button-link"
                    href="www.cnil.fr"
                    label="www.cnil.fr"
                />
            </p>
        </div>
    </div>
</template>

<style scoped>
.confidential-policy-page {
    display: flex;
    width: 100%;
    flex-direction: column;
    gap: 4rem;

    flex: 1;
    .confidential-policy-page__section {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
    }

    ul {
        list-style: circle;
        padding-left: 1.5rem;
    }
    ol {
        list-style: decimal;
        padding-left: 1.5rem;
    }
}

.button-link {
    color: #000000;
}
</style>
